"use client";

import React from "react";
import { ArrowUp, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackServices = ["Roofing Installation", "Roof Repair", "Roof Maintenance", "Waterproofing", "Insulation", "Weatherproofing"];
    return <ul className="space-y-3">
        {fallbackServices.map((service, i) => <li key={i} data-index={i}>
            <Link to="#" className="text-muted-foreground hover:text-primary transition-colors text-sm">
              {service}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="p-3 bg-primary/10 border border-primary/20 rounded-md">
        <p className="text-sm text-foreground font-medium">Thank you for subscribing!</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-2">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="flex gap-2">
                <Input type="email" placeholder="Email address" className="bg-background border-border focus-visible:ring-primary rounded-sm" {...field} />
                <Button type="submit" disabled={isSubmitting} className="rounded-sm px-6">
                  {isSubmitting ? "..." : "Subscribe"}
                </Button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
    </Form>;
}
export default function Footer() {
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  const socialLinks = [{
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "#",
    label: "Twitter"
  }, {
    icon: Linkedin,
    href: "#",
    label: "LinkedIn"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }];
  return <section id="footer" className="bg-background text-foreground pt-16 pb-8 border-t border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645726/img/image_p0dadlls.png" alt="RM Roofing & Waterproofing" className="w-auto object-contain !h-[80px] dark:hidden" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645726/img/image_p0dadlls.png" alt="RM Roofing & Waterproofing" className="h-7 w-auto object-contain hidden dark:block" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Trusted provider of high-quality roofing and waterproofing solutions for residential, commercial, and industrial properties. Committed to top-tier craftsmanship and long-term durability.
            </p>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default font-semibold text-lg tracking-tight">Our Services</h3>
            <MenuProvider menu_id="46">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default font-semibold text-lg tracking-tight">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <span>181-183 Station Lane, Hornchurch, United Kingdom, RM12 6LL</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="size-5 text-primary shrink-0" />
                <Link to="tel:(555)123-4567" className="hover:text-primary transition-colors">
                  (555) 123-4567
                </Link>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-5 text-primary shrink-0" />
                <Link to="mailto:info@rmroofingwaterproofing.com" className="hover:text-primary transition-colors">
                  info@rmroofingwaterproofing.com
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-default font-semibold text-lg tracking-tight">Stay Updated</h3>
            <p className="text-sm text-muted-foreground">
              Subscribe to our newsletter for the latest industry insights and company news.
            </p>
            
            <FormProvider formId="footer-newsletter" sectionName="footer" defaultValues={{
            email: ""
          }}>
              <NewsletterForm />
            </FormProvider>

            <div className="pt-4">
              
            </div>
          </div>
        </div>

        <Separator className="mb-8" />

        {}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">© 2025&nbsp;RM Roofing &amp; Waterproofing. All rights reserved.&nbsp; &nbsp; &nbsp; &nbsp;Company Number: 11449660</p>
          
          <Button variant="outline" size="icon" onClick={scrollToTop} className="rounded-full size-12 border-border bg-foreground hover:bg-foreground/90 group" aria-label="Back to top">
            <ArrowUp className="size-5 text-primary group-hover:-translate-y-1 transition-transform duration-300" />
          </Button>
        </div>
      </div>
    </section>;
}